import { Component, computed, input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { MtrFormFieldModule } from '@mortar/angular/components/atomic/form-field';
import { MtrInputModule } from '@mortar/angular/components/atomic/input';
import { MteStackWrapper } from '@mortar/angular/elements/atomic/stack';
import { DateTime } from 'luxon';
import { computePayWeekRange } from './date-range.utils';
import { Subscription } from 'rxjs';
import { DateWithinWeekEndDateComponent } from './DateWithinWeekEndDate.component';

@Component({
  selector: 'instant-as-local-datetime-within-week-end-date',
  standalone: true,
  template: `
    <mte-stack row a="flex-start">
      <date-within-week-end-date
        [id]="id() + '-date'"
        [inputFormControl]="dateControl"
        [weekEndDate]="weekEndDate()"
        [minDateTimeOverride]="minDateTimeOverride()"
        [maxDateTimeOverride]="maxDateTimeOverride()">
      </date-within-week-end-date>

      <mtr-form-field hideLabel style="width: 80px">
        <input
          mtr-input
          [id]="id() + '-time'"
          type="text"
          inputmode="numeric"
          [formControl]="timeControl"
          mask="Hh:m0"
          [dropSpecialCharacters]="false"
          [showMaskTyped]="true"
          placeHolderCharacter="_"
          (paste)="handleTimePaste($event)" />
      </mtr-form-field>
    </mte-stack>
  `,
  imports: [
    ReactiveFormsModule,
    NgxMaskDirective,
    MtrFormFieldModule,
    MtrInputModule,
    MteStackWrapper,
    DateWithinWeekEndDateComponent,
  ],
})
export class InstantAsLocalDatetimeWithinWeekEndDateComponent implements OnInit, OnDestroy {
  id = input.required<string>();
  inputFormControl = input.required<FormControl<string | null>>();
  weekEndDate = input<string>();
  minDateTimeOverride = input<string>();
  maxDateTimeOverride = input<string>();

  dateControl = new FormControl<string | null>('');
  timeControl = new FormControl<string | null>('');
  subs = new Subscription();

  range = computed(() => {
    const weekRange = computePayWeekRange(this.weekEndDate());
    return {
      min: this.minDateTimeOverride() ?? weekRange.minDateTime,
      max: this.maxDateTimeOverride() ?? weekRange.maxDateTime,
    };
  });

  ngOnInit(): void {
    const sourceControl = this.inputFormControl();

    if (sourceControl.value) {
      this.updateDisplayControls(sourceControl.value);
    }

    this.subs.add(
      sourceControl.valueChanges.subscribe((isoValue) => {
        this.updateDisplayControls(isoValue);
      })
    );

    this.subs.add(this.dateControl.valueChanges.subscribe(() => this.handleInput()));
    this.subs.add(this.timeControl.valueChanges.subscribe(() => this.handleInput()));

    const parentGroup = sourceControl.parent;
    const punchInControl = parentGroup?.get('punchInTime');
    if (punchInControl && punchInControl !== sourceControl) {
      this.subs.add(
        punchInControl.valueChanges.subscribe((punchInIso) => {
          if (!sourceControl.value && !this.dateControl.value && punchInIso) {
            const dt = DateTime.fromISO(punchInIso);
            if (dt.isValid) {
              this.dateControl.setValue(dt.toISODate(), { emitEvent: false });
            }
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private updateDisplayControls(isoValue: string | null): void {
    if (!isoValue) {
      if (!this.inputFormControl().dirty) {
        this.dateControl.setValue('', { emitEvent: false });
        this.timeControl.setValue('', { emitEvent: false });
      }
      return;
    }
    const dt = DateTime.fromISO(isoValue).toLocal();
    const dateStr = dt.toFormat('yyyy-MM-dd');
    const timeStr = dt.toFormat('HH:mm');

    if (this.dateControl.value !== dateStr) {
      this.dateControl.setValue(dateStr, { emitEvent: false });
    }
    if (this.timeControl.value !== timeStr) {
      this.timeControl.setValue(timeStr, { emitEvent: false });
    }
  }

  handleInput(): void {
    this.inputFormControl().markAsDirty();
    let dateValue = this.dateControl.value ?? '';
    const timeValue = this.timeControl.value ?? '';

    if (!dateValue && !timeValue) {
      this.inputFormControl().setValue(null, { emitEvent: false });
      this.inputFormControl().setErrors(null);
      return;
    }

    if (!dateValue && timeValue.length === 5 && !timeValue.includes('_')) {
      let defaultDate: any = DateTime.now().startOf('day');

      const parentGroup = this.inputFormControl().parent;
      const punchInControl = parentGroup?.get('punchInTime');
      if (punchInControl && punchInControl !== this.inputFormControl() && punchInControl.value) {
        const punchInDt = DateTime.fromISO(punchInControl.value);
        if (punchInDt.isValid) {
          defaultDate = punchInDt.startOf('day');
        }
      }

      const { min, max } = this.range();
      if (min && defaultDate < DateTime.fromISO(min).startOf('day')) defaultDate = DateTime.fromISO(min).startOf('day');
      if (max && defaultDate > DateTime.fromISO(max).startOf('day')) defaultDate = DateTime.fromISO(max).startOf('day');

      dateValue = (defaultDate as DateTime).toISODate()!;
      this.dateControl.setValue(dateValue, { emitEvent: false });
    }

    if (dateValue && this.shouldBlockInvalidDate(dateValue)) {
      return;
    }

    if (!dateValue || timeValue.length !== 5 || timeValue.includes('_')) {
      this.inputFormControl().setValue(null, { emitEvent: false });
      this.inputFormControl().setErrors({ invalid: 'Please enter a complete date and time.' });
      return;
    }

    this.commitValue(`${dateValue} ${timeValue}`);
  }

  private shouldBlockInvalidDate(dateValue: string): boolean {
    const partialDate = DateTime.fromFormat(dateValue, 'yyyy-MM-dd', { zone: 'local' });
    if (!partialDate.isValid) {
      this.applyError({ invalid: 'Please enter a valid date.' });
      return true;
    }

    const { min, max } = this.range();
    const minDateTime = min ? DateTime.fromISO(min) : null;
    const maxDateTime = max ? DateTime.fromISO(max) : null;

    if (minDateTime?.isValid && partialDate.startOf('day') < minDateTime.startOf('day')) {
      this.applyError({ min: `Date must be on or after ${minDateTime.toFormat('yyyy-MM-dd')}` });
      return true;
    }

    if (maxDateTime?.isValid && partialDate.startOf('day') > maxDateTime.startOf('day')) {
      this.applyError({ max: `Date must be on or before ${maxDateTime.toFormat('yyyy-MM-dd')}` });
      return true;
    }

    return false;
  }

  handleTimePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text')?.trim() ?? '';
    let timeOutput = '';

    const timeRegex = /^\d{2}:\d{2}$/;
    if (timeRegex.exec(pastedText)) {
      timeOutput = pastedText;
    } else {
      const numbersOnly = pastedText.replaceAll(/\D/g, '');
      if (numbersOnly.length === 4) {
        timeOutput = `${numbersOnly.substring(0, 2)}:${numbersOnly.substring(2)}`;
      }
    }

    if (timeOutput) {
      this.timeControl.setValue(timeOutput);
      this.handleInput();
    }
  }

  private isTimeBoundsInvalid(dateTime: DateTime): boolean {
    const { min, max } = this.range();
    const minD = min ? DateTime.fromISO(min) : null;
    const maxD = max ? DateTime.fromISO(max) : null;

    if (minD?.isValid && dateTime < minD) {
      this.applyError({ min: `Date must be on or after ${minD.toFormat('yyyy-MM-dd HH:mm')}` });
      return true;
    }

    if (maxD?.isValid && dateTime > maxD) {
      this.applyError({ max: `Date must be on or before ${maxD.toFormat('yyyy-MM-dd HH:mm')}` });
      return true;
    }

    return false;
  }

  private commitValue(rawValue: string): void {
    const dateTime = DateTime.fromFormat(rawValue, 'yyyy-MM-dd HH:mm', { zone: 'local' });

    if (!dateTime.isValid) {
      this.applyError({ invalid: 'Please enter a valid date and time.' });
      return;
    }

    const timePart = rawValue.split(' ')[1];
    if (timePart) {
      const [hh, mm] = timePart.split(':').map(Number);
      if (hh > 23 || mm > 59) {
        this.applyError({ invalid: 'Please enter a valid date and time.' });
        return;
      }
    }

    if (this.isTimeBoundsInvalid(dateTime)) {
      return;
    }

    this.inputFormControl().setValue(dateTime.toUTC().toISO());
    this.inputFormControl().setErrors(null);
  }

  private applyError(error: Record<string, string>): void {
    const control = this.inputFormControl();
    control.setErrors(error);
    control.markAsTouched();
  }
}
